"use client";

import {Fragment, JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import type {CheckoutSummaryInstance} from "@/features/booking";

import {BookingDetailsBlock} from "@/entities/booking";
import {BookingSummaryCollapsible} from "@/entities/booking";
import {PropertySummary} from "@/entities/booking";
import {SummaryRow} from "@/entities/booking";
import {translateFeeLabel, translateTaxLabel} from "@/entities/booking";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useTranslation} from "@/shared/i18n/use-translation";
import {assetPath} from "@/shared/utils/asset-path";

import "./booking-summary-card.scss";

/**
 * Translate date string with month key (e.g., "22 mayShort 2026") to current locale
 */
function translateDate(dateStr: string, tCalendar: ReturnType<typeof useTranslation<"calendar">>["t"]): string {
    // Parse "22 mayShort 2026" format (from model)
    const parts = dateStr.split(" ");
    if (parts.length === 3) {
        const day = parts[0]; // "22"
        const monthKey = parts[1]; // "mayShort"
        const year = parts[2]; // "2026"

        // Check if it's a translation key
        if (
            monthKey.endsWith("Short") ||
            monthKey.match(/^(january|february|march|april|may|june|july|august|september|october|november|december)$/i)
        ) {
            const translatedMonth = tCalendar(monthKey as Parameters<typeof tCalendar>[0]);
            return `${day} ${translatedMonth} ${year}`;
        }
    }

    return dateStr;
}

/**
 * Translate guests text (e.g., "1 Adult, 3 Children, 0 Dogs")
 */
function translateGuestsText(guestsText: string, t: ReturnType<typeof useTranslation<"booking">>["t"]): string {
    // Parse "1 Adult, 3 Children, 0 Dogs"
    const parts = guestsText.split(", ");
    const translatedParts = parts.map((part) => {
        const match = part.match(/^(\d+)\s+(.+)$/);
        if (match) {
            const count = parseInt(match[1], 10);
            const label = match[2];

            if (label === "Adult" || label === "Adults") {
                return `${count} ${count === 1 ? t("adult") : t("adults")}`;
            }
            if (label === "Child" || label === "Children") {
                return `${count} ${count === 1 ? t("child") : t("children")}`;
            }
            if (label === "Dog" || label === "Dogs") {
                return t("dogsCount", {count});
            }
        }
        return part;
    });

    return translatedParts.join(", ");
}

interface BookingSummaryCardProps {
    summary: CheckoutSummaryInstance;
    rentalUrl: string;
    className?: string;
}

export function BookingSummaryCard({summary, rentalUrl, className}: BookingSummaryCardProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const isMobile = useMatchMedia({maxWidth: 768});
    const {t, dict} = useTranslation("booking");
    const {t: tCalendar} = useTranslation("calendar");

    const checkIn = summary.booking.checkInFormatted;
    const checkOut = summary.booking.checkOutFormatted;

    const toggleDropdown = (): void => {
        if (isMobile) {
            setIsOpen(!isOpen);
        }
    };

    // Translate fees and taxes rows (optional fees appended to fees section)
    const translatedFeesRows = [
        ...summary.pricing.feesRows.map((fee) => ({...fee, label: translateFeeLabel(fee.label, t, dict)})),
        ...summary.pricing.optionalFeesRows,
    ];

    const translatedTaxesRows = summary.pricing.taxesRows.map((tax) => ({
        ...tax,
        label: translateTaxLabel(tax.label, t, dict),
    }));

    return (
        <div className={`booking-summary-card ${className || ""} ${isMobile ? "booking-summary-card--mobile" : ""}`}>
            {isMobile && (
                <button type="button" className="booking-summary-card__header" onClick={toggleDropdown}>
                    <div className="booking-summary-card__header-left">
                        <SmartImage
                            src={assetPath("/images/icons/house-booking.svg")}
                            alt=""
                            width={24}
                            height={24}
                            className="booking-summary-card__icon"
                        />
                        <span className="booking-summary-card__title">{t("reservationDetails")}</span>
                    </div>
                    <SmartImage
                        src={assetPath("/images/icons/chevron-down-booking.svg")}
                        alt=""
                        width={24}
                        height={24}
                        className={`booking-summary-card__chevron ${isOpen ? "booking-summary-card__chevron--open" : ""}`}
                    />
                </button>
            )}

            <div className={`booking-summary-card__content ${isMobile && !isOpen ? "booking-summary-card__content--collapsed" : ""}`}>
                <PropertySummary
                    href={rentalUrl}
                    imageUrl={summary.property.imageUrl}
                    title={summary.property.title}
                    location={summary.property.location}
                    rating={summary.property.rating}
                    reviewsCount={summary.property.reviewsCount}
                />

                <BookingDetailsBlock
                    left={{
                        label: t("checkInLabel"),
                        value: (
                            <Fragment>
                                <div>{tCalendar(checkIn.weekday as Parameters<typeof tCalendar>[0])}</div>
                                <div>{translateDate(checkIn.date, tCalendar)}</div>
                                <div>{checkIn.time}</div>
                            </Fragment>
                        ),
                    }}
                    right={{
                        label: t("checkOutLabel"),
                        value: (
                            <Fragment>
                                <div>{tCalendar(checkOut.weekday as Parameters<typeof tCalendar>[0])}</div>
                                <div>{translateDate(checkOut.date, tCalendar)}</div>
                                <div>{checkOut.time}</div>
                            </Fragment>
                        ),
                    }}
                />
                <BookingDetailsBlock left={{label: t("guests"), value: translateGuestsText(summary.booking.guestsText, t)}} />

                <SummaryRow label={t("rentNights", {nights: summary.booking.nights})} amount={summary.pricing.rentAmount} />

                {translatedFeesRows.length > 0 && <BookingSummaryCollapsible title={t("fees")} rows={translatedFeesRows} />}

                {translatedTaxesRows.length > 0 && <BookingSummaryCollapsible title={t("taxes")} rows={translatedTaxesRows} />}

                {summary.pricing.couponDiscountAmount && (
                    <SummaryRow label={t("promoDiscount")} amount={summary.pricing.couponDiscountAmount} discount />
                )}
            </div>

            <div className="booking-summary-card__total">
                <SummaryRow label={t("total")} amount={summary.pricing.totalAmount} highlighted />
                {summary.pricing.securityDepositsRows.length > 0 && (
                    <BookingSummaryCollapsible title={t("securityDeposit")} rows={summary.pricing.securityDepositsRows} />
                )}
                {summary.pricing.totalWithDeposits && (
                    <SummaryRow label={t("totalWithDeposits")} amount={summary.pricing.totalWithDeposits} highlightedPlain />
                )}
                {summary.pricing.expectedChargesRows.length > 0 && (
                    <>
                        <hr className="booking-summary-card__divider" />
                        {summary.pricing.expectedChargesRows.map((row, i) => (
                            <SummaryRow
                                key={`charge-${i}`}
                                label={row.labelKey === "dueToday" ? t("dueToday") : t("dueOn", {date: row.date ?? ""})}
                                amount={row.amount}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}
